import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Container,
  Divider,
  FileInput,
  Group,
  Menu,
  Modal,
  Paper,
  Pagination,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowUp,
  IconArrowsExchange,
  IconArrowsSort,
  IconCertificate,
  IconChartBar,
  IconChevronDown,
  IconClipboardText,
  IconFileSpreadsheet,
  IconPaperclip,
  IconPlus,
  IconQrcode,
  IconSearch,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { Batch, Customer, DeliveryType, Shipment } from "../api/types";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { ExportActModal } from "../components/ExportActModal";
import { ExportToSheetModal } from "../components/shipments/ExportToSheetModal";
import { DeliveryTypesModal } from "../components/shipments/DeliveryTypesModal";
import { ShipmentDocuments } from "../components/shipments/ShipmentDocuments";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { ShipmentRowMenu } from "../components/shipments/ShipmentRowMenu";
import { BulkDocumentsModal, useStatusDocsGate } from "../components/shipments/StatusDocsGate";
import { ShipmentStatusBadges } from "../components/shipments/ShipmentStatusBadges";
import { deliveryTypeLabel, shipmentStatusLabel } from "../lib/shipmentLabels";
import { confirmWarrantyContract } from "../lib/warranty";
import { formatTypeCounts } from "../lib/typeCounts";
import { useCurrentUser } from "../hooks/useCurrentUser";

type ShipmentSortKey = "code" | "customer" | "location" | "shipped_date" | "status" | "device_count";
type SortDir = "asc" | "desc";

export function Shipments() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canEdit = user.role === "admin" || user.role === "operator";
  const isAdmin = user.role === "admin";
  const navigate = useNavigate();

  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [bulkDocsOpen, setBulkDocsOpen] = useState(false);
  const docsGate = useStatusDocsGate();
  const [sortKey, setSortKey] = useState<ShipmentSortKey>("shipped_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [perPage, setPerPage] = useState<number>(30);
  const [page, setPage] = useState<number>(1);
  const [onlyDebt, setOnlyDebt] = useState(false);
  const [docsShipmentId, setDocsShipmentId] = useState<number | null>(null);
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryType[]>([]);
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [custModalValue, setCustModalValue] = useState<string | null>(null);

  async function loadShipments(q: string) {
    setLoading(true);
    try {
      setShipments(await api.listShipments({ q: q || undefined }));
    } catch {
      notifications.show({ color: "red", title: t("common.error"), message: t("shipments.loadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    Promise.all([api.listCustomers(), api.listBatches()])
      .then(([c, b]) => {
        setCustomers(c);
        setBatches(b);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    void api.listDeliveryTypes(true).then(setDeliveryTypes).catch(() => undefined);
  }, []);

  useEffect(() => {
    const tm = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(tm);
  }, [search]);

  useEffect(() => {
    loadShipments(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  async function handleScanDetect(raw: string) {
    // Strip EZPL/QR artifacts and pull the shipment code out of any URL shape:
    //   "https://server.fst.md/sh/GM-LW-007"  → "GM-LW-007"
    //   "https://server.fst.md/GM-LW-007"     → "GM-LW-007"
    //   "GM-LW-007"                            → "GM-LW-007"
    let code = raw
      .trim()
      .replace(/^:+/, "")
      .replace(/[:!]+$/, "")
      .split(/\s+/)[0];
    // If a URL — keep only the last non-empty path segment
    const urlMatch = code.match(/^https?:\/\/[^/]+\/(.*)$/i);
    if (urlMatch) {
      const path = urlMatch[1].replace(/^sh\//i, "");
      code = path.split(/[/?#]/).filter(Boolean).pop() ?? "";
    }
    if (!code) {
      notifications.show({
        color: "red",
        message: t("shipments.noMatch", { code: raw }),
      });
      return;
    }
    try {
      const matches = await api.listShipments({ q: code });
      const exact = matches.find((s) => s.code.toLowerCase() === code.toLowerCase());
      const hit = exact ?? (matches.length === 1 ? matches[0] : null);
      if (hit) {
        setScanOpen(false);
        navigate(`/shipments/${hit.id}`);
      } else if (matches.length > 1) {
        notifications.show({
          color: "yellow",
          message: t("shipments.ambiguous", { count: matches.length, code }),
        });
      } else {
        notifications.show({
          color: "red",
          message: t("shipments.noMatch", { code }),
        });
      }
    } catch {
      notifications.show({ color: "red", message: t("shipments.searchError") });
    }
  }

  const customerById = useMemo(
    () => new Map(customers.map((c) => [c.id, c.name])),
    [customers],
  );

  function toggleSort(key: ShipmentSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "shipped_date" || key === "device_count" ? "desc" : "asc");
    }
  }

  const displayedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = onlyDebt ? shipments.filter((s) => s.on_debt) : [...shipments];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "code": av = a.code; bv = b.code; break;
        case "customer":
          av = a.customer_id == null ? "" : (customerById.get(a.customer_id) ?? "");
          bv = b.customer_id == null ? "" : (customerById.get(b.customer_id) ?? "");
          break;
        case "location": av = a.location ?? ""; bv = b.location ?? ""; break;
        case "shipped_date":
          av = a.shipped_date ?? -Infinity;
          bv = b.shipped_date ?? -Infinity;
          break;
        case "status": av = a.status; bv = b.status; break;
        case "device_count": av = a.device_count; bv = b.device_count; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [shipments, sortKey, sortDir, customerById, onlyDebt]);

  const pageCount = Math.max(1, Math.ceil(displayedRows.length / perPage));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => displayedRows.slice((currentPage - 1) * perPage, currentPage * perPage),
    [displayedRows, currentPage, perPage],
  );
  // Reset to the first page when the result set or page size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, onlyDebt, perPage]);

  function SortHeader({ k, label, style }: { k: ShipmentSortKey; label: string; style?: React.CSSProperties }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600, ...style }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  const allOnPageSelected = pagedRows.length > 0 &&
    pagedRows.every((s) => selectedIds.has(s.id));
  function toggleAllOnPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pagedRows.forEach((s) => next.delete(s.id));
      } else {
        pagedRows.forEach((s) => next.add(s.id));
      }
      return next;
    });
  }
  function toggleOne(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function markDebt(ids: number[], onDebt: boolean) {
    if (ids.length === 0) return;
    try {
      const res = await api.markShipmentsDebt(ids, onDebt);
      notifications.show({ color: "green", message: t("shipments.debtUpdated", { count: res.updated }) });
      await loadShipments(debouncedSearch);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function bulkConfirm(kind: "chirpstack" | "accounting", dir: "do" | "undo") {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const call =
        kind === "chirpstack"
          ? dir === "do" ? api.bulkChirpstackDone(ids) : api.bulkChirpstackUndo(ids)
          : dir === "do" ? api.bulkAccountingConfirm(ids) : api.bulkAccountingUndo(ids);
      const res = await call;
      const msgKey =
        kind === "chirpstack"
          ? dir === "do" ? "shipments.bulkChirpstackDone" : "shipments.bulkChirpstackUndone"
          : dir === "do" ? "shipments.bulkAccountingDone" : "shipments.bulkAccountingUndone";
      notifications.show({ color: "green", message: t(msgKey, { count: res.updated }) });
      setSelectedIds(new Set());
      await loadShipments(debouncedSearch);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  function bulkStatus(status: string) {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // Shipped/Delivered: ask first whether there's a document/photo to attach.
    docsGate.request(ids, status, async () => {
      try {
        const res = await api.bulkSetStatus(ids, status);
        notifications.show({ color: "green", message: t("shipments.bulkStatusDone", { count: res.updated }) });
        setSelectedIds(new Set());
        await loadShipments(debouncedSearch);
        return true;
      } catch (e) {
        notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
        return false;
      }
    });
  }

  async function bulkCustomer(customerId: number | null) {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await api.bulkSetCustomer(ids, customerId);
      notifications.show({ color: "green", message: t("shipments.bulkCustomerDone", { count: res.updated }) });
      setSelectedIds(new Set());
      await loadShipments(debouncedSearch);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function changeType(s: Shipment, typeId: string | null) {
    if (!typeId) return;
    try {
      const updated = await api.updateShipment(s.id, { delivery_type_id: Number(typeId) });
      setShipments((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  const selectedShipments = shipments.filter((s) => selectedIds.has(s.id));
  const allOnDebt = selectedShipments.length > 0 && selectedShipments.every((s) => s.on_debt);
  const allChirpstack =
    selectedShipments.length > 0 && selectedShipments.every((s) => s.chirpstack_added_at);
  const allAccounting =
    selectedShipments.length > 0 && selectedShipments.every((s) => s.accounting_confirmed_at);
  const STATUS_VALUES = ["draft", "ready", "shipped", "delivered"];

  return (
    <Container size="xl">
      <Stack mb="md" gap="sm">
        <Title order={2}>{t("shipments.title")}</Title>
        <Group gap="xs" wrap="wrap">
          <Button
            variant="light"
            leftSection={<IconQrcode size={16} />}
            onClick={() => setScanOpen(true)}
          >
            {t("shipments.scanBox")}
          </Button>
          <Button
            variant="default"
            leftSection={<IconChartBar size={16} />}
            onClick={() => navigate("/shipment-stats")}
          >
            {t("shipments.viewStats")}
          </Button>
          {canEdit && (
            <Button
              variant="default"
              onClick={() => setTypesModalOpen(true)}
            >
              {t("shipments.deliveryTypes.manageButton")}
            </Button>
          )}
          {canEdit && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setModalOpen(true)}>
              {t("shipments.create")}
            </Button>
          )}
        </Group>
      </Stack>

      <TextInput
        mb="md"
        placeholder={t("shipments.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          search ? (
            <ActionIcon variant="subtle" color="gray" onClick={() => setSearch("")}>
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
      />

      <Switch
        mb="md"
        label={t("shipments.onlyDebt")}
        checked={onlyDebt}
        onChange={(e) => setOnlyDebt(e.currentTarget.checked)}
      />

      {selectedIds.size > 0 && (
        <Paper withBorder p="xs" mb="sm">
          <Group justify="space-between">
            <Text size="sm">
              {t("shipments.selectedCount", { count: selectedIds.size })}
            </Text>
            <Group gap="xs">
              <Menu shadow="md" position="bottom-end">
                <Menu.Target>
                  <Button size="xs" rightSection={<IconChevronDown size={14} />}>
                    {t("shipments.exportMenu")}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconClipboardText size={14} />} onClick={() => setExportOpen(true)}>
                    {t("shipments.exportAct")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileSpreadsheet size={14} />}
                    onClick={() => {
                      window.location.href = api.exportShipmentsCombinedUrl(Array.from(selectedIds));
                    }}
                  >
                    {t("shipments.exportCombinedXlsx")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileSpreadsheet size={14} />}
                    onClick={() => {
                      window.location.href = api.exportShipmentsCombinedUrl(Array.from(selectedIds), { accounting: false });
                    }}
                  >
                    {t("shipments.exportCombinedXlsxFull")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCertificate size={14} />}
                    onClick={() => {
                      const custs = new Set(
                        shipments.filter((s) => selectedIds.has(s.id)).map((s) => s.customer_id),
                      );
                      if (custs.size !== 1 || [...custs][0] == null) {
                        notifications.show({ color: "red", message: t("shipments.exportWarrantyMixed") });
                        return;
                      }
                      const cust = customers.find((c) => c.id === [...custs][0]);
                      if (!confirmWarrantyContract(cust, t)) return;
                      window.location.href = api.exportWarrantyUrl(Array.from(selectedIds));
                    }}
                  >
                    {t("shipments.exportWarranty")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCertificate size={14} />}
                    onClick={() => {
                      const custs = new Set(
                        shipments.filter((s) => selectedIds.has(s.id)).map((s) => s.customer_id),
                      );
                      if (custs.size !== 1 || [...custs][0] == null) {
                        notifications.show({ color: "red", message: t("shipments.exportWarrantyMixed") });
                        return;
                      }
                      const cust = customers.find((c) => c.id === [...custs][0]);
                      if (!confirmWarrantyContract(cust, t)) return;
                      window.location.href = api.exportWarrantyPdfUrl(Array.from(selectedIds));
                    }}
                  >
                    {t("shipments.exportWarrantyPdf")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Button size="xs" variant="light" onClick={() => setSheetOpen(true)}>
                {t("shipments.exportSheet")}
              </Button>
              {canEdit && (
                <>
                  <Menu shadow="md" position="bottom-end">
                    <Menu.Target>
                      <Button size="xs" variant="default" rightSection={<IconChevronDown size={14} />}>
                        {t("shipments.changeStatus")}
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {STATUS_VALUES.map((st) => (
                        <Menu.Item key={st} leftSection={<IconArrowsExchange size={14} />} onClick={() => void bulkStatus(st)}>
                          {shipmentStatusLabel(t, st)}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconPaperclip size={14} />}
                    onClick={() => setBulkDocsOpen(true)}
                  >
                    {t("shipments.docsGate.bulkButton")}
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => { setCustModalValue(null); setCustModalOpen(true); }}
                  >
                    {t("shipments.bulkCustomer")}
                  </Button>
                  {allOnDebt ? (
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      onClick={() => void markDebt(Array.from(selectedIds), false).then(() => setSelectedIds(new Set()))}
                    >
                      {t("shipments.unmarkDebt")}
                    </Button>
                  ) : (
                    <Button
                      size="xs"
                      color="red"
                      onClick={() => void markDebt(Array.from(selectedIds), true).then(() => setSelectedIds(new Set()))}
                    >
                      {t("shipments.markDebt")}
                    </Button>
                  )}
                  {allChirpstack ? (
                    isAdmin && (
                      <Button size="xs" variant="default" onClick={() => void bulkConfirm("chirpstack", "undo")}>
                        {t("shipments.bulkChirpstackUndo")}
                      </Button>
                    )
                  ) : (
                    <Button size="xs" variant="default" onClick={() => void bulkConfirm("chirpstack", "do")}>
                      {t("shipments.bulkChirpstack")}
                    </Button>
                  )}
                  {allAccounting ? (
                    isAdmin && (
                      <Button size="xs" variant="default" onClick={() => void bulkConfirm("accounting", "undo")}>
                        {t("shipments.bulkAccountingUndo")}
                      </Button>
                    )
                  ) : (
                    <Button size="xs" variant="default" onClick={() => void bulkConfirm("accounting", "do")}>
                      {t("shipments.bulkAccounting")}
                    </Button>
                  )}
                </>
              )}
              <Button size="xs" variant="subtle" onClick={() => setSelectedIds(new Set())}>
                {t("common.clearSelection")}
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <ResponsiveTable
        stickyColumns={2}
        mobile={{
          rows: pagedRows,
          sort: {
            options: [
              { value: "code", label: t("shipments.colCode") },
              { value: "customer", label: t("shipments.colCustomer") },
              { value: "location", label: t("shipments.colLocation") },
              { value: "shipped_date", label: t("shipments.colDate") },
              { value: "status", label: t("shipments.colStatus") },
              { value: "device_count", label: t("shipments.colDevices") },
            ],
            value: sortKey,
            dir: sortDir,
            onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as ShipmentSortKey)),
          },
          rowKey: (s) => s.id,
          title: (s) => (s.customer_id == null ? t("shipments.internalStock") : customers.find((c) => c.id === s.customer_id)?.name ?? `#${s.customer_id}`),
          subtitle: (s) => [s.code, s.shipped_date ?? t("common.none"), formatTypeCounts(t, s.type_counts, s.device_count)].join(" · "),
          badge: (s) => (
            <>
              {s.has_documents && (
                <ActionIcon
                  variant="light"
                  size="sm"
                  color="gray"
                  title={t("shipments.docs.openDocs")}
                  aria-label={t("shipments.docs.openDocs")}
                  onClick={(e) => { e.stopPropagation(); setDocsShipmentId(s.id); }}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <IconPaperclip size={14} />
                </ActionIcon>
              )}
              <ShipmentStatusBadges shipment={s} compact showType={false} />
            </>
          ),
          fields: [
            { label: t("shipments.colLocation"), render: (s) => s.location ?? t("common.none") },
            {
              label: t("shipments.deliveryTypeLabel"),
              render: (s) => canEdit ? (
                <Select
                  data={deliveryTypes.map((dt) => ({ value: String(dt.id), label: deliveryTypeLabel(t, dt.slug, dt.name) }))}
                  value={s.delivery_type_id ? String(s.delivery_type_id) : null}
                  onChange={(v) => void changeType(s, v)}
                  size="xs"
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true }}
                />
              ) : (
                <Text c="dimmed" size="xs">—</Text>
              ),
            },
          ],
          actions: (s) => (
            <ShipmentRowMenu
              shipment={s}
              canEdit={canEdit}
              isAdmin={user.role === "admin"}
              onOpen={() => navigate(`/shipments/${s.id}`)}
              onOpenDocs={() => setDocsShipmentId(s.id)}
              onChanged={() => void loadShipments(debouncedSearch)}
            />
          ),
          onOpen: (s) => navigate(debouncedSearch ? `/shipments/${s.id}?q=${encodeURIComponent(debouncedSearch)}` : `/shipments/${s.id}`),
          selection: {
            isSelected: (s) => selectedIds.has(s.id),
            toggle: (s) => toggleOne(s.id),
            allSelected: allOnPageSelected,
            someSelected: pagedRows.some((s) => selectedIds.has(s.id)),
            toggleAll: toggleAllOnPage,
          },
          rowBg: (s) => (s.chirpstack_added_at ? "var(--mantine-color-green-light)" : undefined),
          loading: loading && shipments.length === 0,
          empty: debouncedSearch ? t("shipments.emptySearch", { q: debouncedSearch }) : t("shipments.empty"),
        }}
      >
      <Table.ScrollContainer minWidth={900}>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={36}>
              <Checkbox
                checked={allOnPageSelected}
                indeterminate={!allOnPageSelected && pagedRows.some((s) => selectedIds.has(s.id))}
                onChange={toggleAllOnPage}
                aria-label={t("shipments.selectAll")}
              />
            </Table.Th>
            <Table.Th><SortHeader k="code" label={t("shipments.colCode")} /></Table.Th>
            <Table.Th><SortHeader k="customer" label={t("shipments.colCustomer")} /></Table.Th>
            <Table.Th><SortHeader k="location" label={t("shipments.colLocation")} /></Table.Th>
            <Table.Th><SortHeader k="shipped_date" label={t("shipments.colDate")} /></Table.Th>
            <Table.Th style={{ whiteSpace: "nowrap" }}><SortHeader k="status" label={t("shipments.colStatus")} style={{ whiteSpace: "nowrap" }} /></Table.Th>
            <Table.Th>{t("shipments.deliveryTypeLabel")}</Table.Th>
            <Table.Th><SortHeader k="device_count" label={t("shipments.colDevices")} /></Table.Th>
            <Table.Th w={40} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={9}>
                <Text c="dimmed">{t("common.loading")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && shipments.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={9}>
                <Text c="dimmed">
                  {debouncedSearch
                    ? t("shipments.emptySearch", { q: debouncedSearch })
                    : t("shipments.empty")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {pagedRows.map((s) => {
            const cust = customers.find((c) => c.id === s.customer_id);
            const linkTarget = debouncedSearch
              ? `/shipments/${s.id}?q=${encodeURIComponent(debouncedSearch)}`
              : `/shipments/${s.id}`;
            return (
              <Table.Tr
                key={s.id}
                bg={s.chirpstack_added_at ? "var(--mantine-color-green-light)" : undefined}
              >
                <Table.Td>
                  <Checkbox
                    checked={selectedIds.has(s.id)}
                    onChange={() => toggleOne(s.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t("shipments.selectRow")}
                  />
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Anchor component={Link} to={linkTarget} fw={500}>
                      {s.code}
                    </Anchor>
                    {s.has_documents && (
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="gray"
                        title={t("shipments.docs.openDocs")}
                        onClick={(e) => { e.stopPropagation(); setDocsShipmentId(s.id); }}
                      >
                        <IconPaperclip size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {s.customer_id == null ? (
                    <Badge variant="light" color="gray">
                      {t("shipments.internalStock")}
                    </Badge>
                  ) : (
                    cust?.name ?? `#${s.customer_id}`
                  )}
                </Table.Td>
                <Table.Td>{s.location ?? t("common.none")}</Table.Td>
                <Table.Td>{s.shipped_date ?? t("common.none")}</Table.Td>
                <Table.Td>
                  <ShipmentStatusBadges shipment={s} compact showType={false} />
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {canEdit ? (
                    <Select
                      data={deliveryTypes.map((dt) => ({ value: String(dt.id), label: deliveryTypeLabel(t, dt.slug, dt.name) }))}
                      value={s.delivery_type_id ? String(s.delivery_type_id) : null}
                      onChange={(v) => void changeType(s, v)}
                      size="xs"
                      w={150}
                      allowDeselect={false}
                      comboboxProps={{ withinPortal: true }}
                    />
                  ) : (
                    <Text c="dimmed" size="xs">—</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge color="blue" variant="light">
                    {formatTypeCounts(t, s.type_counts, s.device_count)}
                  </Badge>
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <ShipmentRowMenu
                    shipment={s}
                    canEdit={canEdit}
                    isAdmin={user.role === "admin"}
                    onOpen={() => navigate(`/shipments/${s.id}`)}
                    onOpenDocs={() => setDocsShipmentId(s.id)}
                    onChanged={() => void loadShipments(debouncedSearch)}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>

      {!loading && displayedRows.length > 0 && (
        <Group justify="space-between" mt="md" wrap="wrap" gap="sm">
          <Group gap="xs" align="center">
            <Text size="sm" c="dimmed">{t("shipments.perPage")}</Text>
            <Select
              size="xs"
              w={80}
              value={String(perPage)}
              onChange={(v) => setPerPage(Number(v) || 30)}
              data={["30", "50", "100"]}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
            <Text size="sm" c="dimmed">
              {t("shipments.pageRange", {
                from: (currentPage - 1) * perPage + 1,
                to: Math.min(currentPage * perPage, displayedRows.length),
                total: displayedRows.length,
              })}
            </Text>
          </Group>
          {pageCount > 1 && (
            <Pagination total={pageCount} value={currentPage} onChange={setPage} size="sm" />
          )}
        </Group>
      )}

      <CreateShipmentModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        customers={customers}
        batches={batches}
        deliveryTypes={deliveryTypes}
        onCreated={() => {
          setModalOpen(false);
          loadShipments(debouncedSearch);
        }}
      />

      <DeliveryTypesModal
        opened={typesModalOpen}
        onClose={() => setTypesModalOpen(false)}
        onChanged={() => void api.listDeliveryTypes(true).then(setDeliveryTypes)}
      />

      <Modal
        opened={custModalOpen}
        onClose={() => setCustModalOpen(false)}
        title={t("shipments.bulkCustomerTitle", { count: selectedIds.size })}
        centered
      >
        <Stack>
          <Select
            label={t("shipments.bulkCustomerLabel")}
            placeholder={t("shipments.bulkCustomerInternal")}
            data={customers.map((c) => ({ value: String(c.id), label: c.name }))}
            value={custModalValue}
            onChange={setCustModalValue}
            searchable
            clearable
          />
          <Text size="xs" c="dimmed">{t("shipments.bulkCustomerHint")}</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCustModalOpen(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => {
                void bulkCustomer(custModalValue ? Number(custModalValue) : null);
                setCustModalOpen(false);
              }}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={scanOpen}
        onClose={() => setScanOpen(false)}
        title={t("shipments.scanModalTitle")}
        size="md"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {t("shipments.scanModalHint")}
          </Text>
          <BarcodeScanner
            active={scanOpen}
            onDetect={(raw) => void handleScanDetect(raw)}
          />
        </Stack>
      </Modal>

      <ExportActModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        shipmentIds={Array.from(selectedIds)}
      />
      <ExportToSheetModal
        opened={sheetOpen}
        onClose={() => setSheetOpen(false)}
        target="shipments"
        ids={Array.from(selectedIds)}
      />

      <BulkDocumentsModal
        opened={bulkDocsOpen}
        onClose={() => setBulkDocsOpen(false)}
        shipmentIds={Array.from(selectedIds)}
      />
      {docsGate.element}

      <Modal
        opened={docsShipmentId !== null}
        onClose={() => setDocsShipmentId(null)}
        title={t("shipments.docs.modalTitle")}
        centered
        size="lg"
      >
        {docsShipmentId !== null && (
          <ShipmentDocuments shipmentId={docsShipmentId} canEdit={canEdit} />
        )}
      </Modal>
    </Container>
  );
}

interface CreateProps {
  opened: boolean;
  onClose: () => void;
  customers: Customer[];
  batches: Batch[];
  deliveryTypes: DeliveryType[];
  onCreated: () => void;
}

function CreateShipmentModal({ opened, onClose, customers, batches, deliveryTypes, onCreated }: CreateProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [sourceMode, setSourceMode] = useState<"batch" | "file" | "paste" | "none">(
    "none",
  );
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importText, setImportText] = useState("");

  const form = useForm<{
    code: string;
    customer_id: string;
    batch_id: string;
    shipped_date: string;
    location: string;
    branch: string;
    notes: string;
    delivery_type_id: string | null;
  }>({
    initialValues: {
      code: "",
      customer_id: "",
      batch_id: "",
      shipped_date: "",
      location: "",
      branch: "",
      notes: "",
      delivery_type_id: null,
    },
    validate: {
      code: (v) => (v.trim().length === 0 ? t("shipments.codeRequired") : null),
    },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      const normal = deliveryTypes.find((dt) => dt.slug === "normal");
      form.setFieldValue("delivery_type_id", normal ? String(normal.id) : null);
      void api.nextShipmentCode().then((r) => form.setFieldValue("code", r.code)).catch(() => undefined);
      setSourceMode("none");
      setImportFile(null);
      setImportText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  useEffect(() => {
    const cust = customers.find((c) => String(c.id) === form.values.customer_id);
    if (cust) {
      if (cust.city) form.setFieldValue("location", cust.city);
      if (cust.branch) form.setFieldValue("branch", cust.branch);
      form.setFieldValue("shipped_date", new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.customer_id]);

  async function handleSubmit(values: typeof form.values) {
    setSubmitting(true);
    try {
      const payload: Parameters<typeof api.createShipment>[0] = {
        code: values.code,
        customer_id: values.customer_id ? Number(values.customer_id) : null,
        shipped_date: values.shipped_date || null,
        location: values.location || null,
        branch: values.branch || null,
        notes: values.notes || null,
        delivery_type_id: values.delivery_type_id ? Number(values.delivery_type_id) : undefined,
      };
      if (sourceMode === "batch" && values.batch_id) {
        payload.batch_id = Number(values.batch_id);
      }
      const r = await api.createShipment(payload);
      const newShipmentId = r.shipment.id;
      let extra = "";

      if (sourceMode === "file" && importFile) {
        const imp = await api.importShipmentFile(newShipmentId, importFile);
        extra = t("shipments.fromFile", {
          applied: imp.applied.length,
          notFound: imp.not_found.length,
          conflicts: imp.conflicts.length,
        });
      } else if (sourceMode === "paste" && importText.trim()) {
        const imp = await api.importShipmentText(newShipmentId, importText);
        extra = t("shipments.fromText", {
          applied: imp.applied.length,
          notFound: imp.not_found.length,
          conflicts: imp.conflicts.length,
        });
      }

      notifications.show({
        color: "green",
        message: t("shipments.createdToast", { count: r.assigned_devices, extra }),
        autoClose: 5000,
      });
      onCreated();
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.detail as { error?: string })?.error === "shipment_exists"
      ) {
        void api
          .nextShipmentCode()
          .then((r) => form.setFieldValue("code", r.code))
          .catch(() => undefined);
        notifications.show({ color: "yellow", message: t("shipments.codeTaken") });
        return;
      }
      const status = err instanceof ApiError ? err.status : 0;
      notifications.show({
        color: "red",
        title: t("common.error"),
        message: status === 409 ? t("shipments.codeExists") : t("shipments.createFailed"),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("shipments.createTitle")} size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput label={t("shipments.codeLabel")} required placeholder="SH24" {...form.getInputProps("code")} />
          <Select
            label={t("shipments.customerLabel")}
            placeholder={t("shipments.customerPlaceholder")}
            description={t("shipments.customerDesc")}
            data={customers.map((c) => ({ value: String(c.id), label: c.name }))}
            searchable
            clearable
            {...form.getInputProps("customer_id")}
          />
          <Select
            label={t("shipments.deliveryTypeLabel")}
            data={deliveryTypes.map((dt) => ({ value: String(dt.id), label: deliveryTypeLabel(t, dt.slug, dt.name) }))}
            clearable
            {...form.getInputProps("delivery_type_id")}
          />
          <Divider label={t("shipments.sourceDivider")} labelPosition="center" />
          <Tabs value={sourceMode} onChange={(v) => v && setSourceMode(v as typeof sourceMode)}>
            <Tabs.List>
              <Tabs.Tab value="batch">{t("shipments.tabBatch")}</Tabs.Tab>
              <Tabs.Tab value="file" leftSection={<IconUpload size={12} />}>
                {t("shipments.tabFile")}
              </Tabs.Tab>
              <Tabs.Tab value="paste" leftSection={<IconClipboardText size={12} />}>
                {t("shipments.tabPaste")}
              </Tabs.Tab>
              <Tabs.Tab value="none">{t("shipments.tabNone")}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="batch" pt="sm">
              <Select
                label={t("shipments.batchSelectLabel")}
                placeholder={t("shipments.batchSelectPlaceholder")}
                data={batches.map((b) => ({
                  value: String(b.id),
                  label: `${b.code} (${b.device_count} device)`,
                }))}
                clearable
                searchable
                {...form.getInputProps("batch_id")}
              />
            </Tabs.Panel>

            <Tabs.Panel value="file" pt="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  {t("shipments.fileHint")}
                </Text>
                <FileInput
                  placeholder={t("shipments.filePickerPlaceholder")}
                  accept=".xlsx,.xlsm,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  value={importFile}
                  onChange={setImportFile}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="paste" pt="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  {t("shipments.pasteHint")}
                </Text>
                <Textarea
                  placeholder={"aabbccddeeff0011\n3020.50000002.2511\n..."}
                  autosize
                  minRows={4}
                  maxRows={10}
                  value={importText}
                  onChange={(e) => setImportText(e.currentTarget.value)}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="none" pt="sm">
              <Text size="xs" c="dimmed">
                {t("shipments.noneHint")}
              </Text>
            </Tabs.Panel>
          </Tabs>
          <Divider />
          <TextInput
            label={t("shipments.locationLabel")}
            placeholder="COMRAT"
            {...form.getInputProps("location")}
          />
          <TextInput
            label={t("shipments.branchLabel")}
            placeholder="GAGAUZ GAZ"
            description={t("shipments.branchDesc")}
            {...form.getInputProps("branch")}
          />
          <TextInput
            label={t("shipments.shippedDateLabel")}
            placeholder="YYYY-MM-DD"
            {...form.getInputProps("shipped_date")}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={submitting}>
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
